// Third-party
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Waypoint } from 'react-waypoint';

// Redux
import { ReduxState } from '../../../../redux/modules/types';
import { addEnterpriseMember, deleteEnterpriseMember, modifyEnterpriseMember } from '../../../../redux/modules/enterprise/admin/members';
import { addEnterpriseGroupMember, deleteEnterpriseGroupMember } from '../../../../redux/modules/enterprise/admin/groups';

// Components
import DataTable from '../../../components/data/DataTable';
import { PrimaryButton, IconButton } from '../../../components/buttons';
import { AddEnterpriseMemberModal } from '../../../components/modals';
import { DestructiveModal } from '@capintel/component-lib/destructive-modal';
import { Stack } from '@capintel/component-lib/stack';
import SearchInput from '../../../components/inputs/SearchInput';
import ModifyEnterpriseMemberModal from '../../../components/modals/ModifyEnterpriseMemberModal';
import AddEnterpriseGroupMemberModal from '../../../components/modals/AddEnterpriseGroupMemberModal';
import { GenericEmptyState } from '../../../components/misc';
import { InfoTooltip } from '../../../components/tooltips';

// Utils
import { formatMessage, FormattedMessage } from '../../../../utils/i18n';
import notification from '../../../../utils/notification';
import { searchObjectsOnFields } from '../../../../utils/search';
import { ENTERPRISE_GROUP_NAME, getEnterpriseGroup, isEnterpriseGroup } from '../../../../utils/enterprise';
import { PERMISSIONS, userHasPermission, ENTERPRISE_ADMIN_PERMISSIONS, userHasPermissionEnabled } from '../../../../utils/permissions';
import EnterpriseMember from '@/types/enterprise-member';
import EnterpriseAdminDetails from '@/types/enterprise-admin-details';
import User from '@/types/user';
import EnterpriseAdminGroup from '@/types/enterprise-admin-group';

type State = {
  showAddMemberModal: boolean;
  showModifyMemberModal: boolean;
  showDeleteMembersModal: boolean;
  membersToDelete: EnterpriseMember[];
  memberToUpdate?: EnterpriseMember;
  query: string;
  memberDisplayMultiplier: number;
};

type StateProps = {
  user: User;
  adminDetails: EnterpriseAdminDetails;
};

type ParentProps = {
  group?: EnterpriseAdminGroup;
};

type DispatchProps = {
  group: EnterpriseAdminGroup;
  addEnterpriseGroupMember: (groupId: string, userId: string) => Promise<EnterpriseMember>;
  deleteEnterpriseGroupMember: (groupId: string, userId: string) => Promise<void>;
  addEnterpriseMember: (fname: string, lname: string, email: string, uid: string, nameId: string) => Promise<EnterpriseMember>;
  modifyEnterpriseMember: (userId: string, uid: string, nameId: string) => Promise<void>;
  deleteEnterpriseMember: (userId: string) => Promise<void>;
};

type Props = ParentProps & StateProps & DispatchProps;

class EnterpriseMembers extends Component<Props, State> {
  readonly state: State = {
    showAddMemberModal: false,
    showModifyMemberModal: false,
    showDeleteMembersModal: false,
    membersToDelete: [],
    memberToUpdate: undefined,
    query: '',
    memberDisplayMultiplier: 1,
  };

  removeSelectedMembers = () => {
    const { user, group } = this.props;
    const { membersToDelete } = this.state;

    if (this == null && user == null) {
      return;
    }

    // Prevent admins from deleting themselves from the enterprise.
    if (group.name === ENTERPRISE_GROUP_NAME && membersToDelete.some(member => member._id === user.id)) {
      notification.error(formatMessage('Admin.Enterprise.SelfDeleteError'));
      return;
    }

    this.hideDeleteMembersModal();

    for (const member of membersToDelete) {
      if (group.name === ENTERPRISE_GROUP_NAME) {
        this.props.deleteEnterpriseMember(member._id);
      } else {
        this.props.deleteEnterpriseGroupMember(group._id, member._id);
      }
    }
  };

  getColumns = () => {
    const { group, user } = this.props;
    const hasMemberRemovePermission = userHasPermissionEnabled(
      user,
      PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS,
      PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS_REMOVE,
    );
    const columns = [
      {
        Header: formatMessage('Admin.Enterprise.Fields.FirstName'),
        accessor: 'fname',
        className: 'enterprise-member-name',
        width: 150,
        hidden: false,
      },
      {
        Header: formatMessage('Admin.Enterprise.Fields.LastName'),
        accessor: 'lname',
        width: 150,
        hidden: false,
      },
      {
        Header: formatMessage('Admin.Enterprise.Fields.Email'),
        accessor: 'email',
        width: group.name === ENTERPRISE_GROUP_NAME ? 400 : undefined,
        hidden: false,
      },
      {
        Header: formatMessage('Admin.Enterprise.Fields.Role'),
        accessor: 'features',
        Cell: ({ original }: { original: EnterpriseMember }) => {
          const features = original.features.filter(feature => ENTERPRISE_ADMIN_PERMISSIONS.includes(feature));
          if (features.length > 0) {
            const tooltip: string[] = [];
            for (const feature of features) {
              tooltip.push(formatMessage(`AdminPermissions.${feature}`));
            }

            return (
              <>
                <span data-private data-dd-privacy="mask">
                  Admin&nbsp;
                </span>
                <InfoTooltip message={tooltip.join(', ')} />
              </>
            );
          }
          return <></>;
        },
        hidden: group.name !== ENTERPRISE_GROUP_NAME,
      },
      {
        Header: formatMessage('Admin.Enterprise.Fields.MemberID'),
        accessor: 'uid',
        hidden: !userHasPermission(user, PERMISSIONS.ADMIN_SECTION_SHOW_USER_UID) || group.name !== ENTERPRISE_GROUP_NAME,
      },
      {
        Header: formatMessage('Admin.Enterprise.Fields.NameID'),
        accessor: 'nameId',
        hidden: !userHasPermission(user, PERMISSIONS.ADMIN_SECTION_SHOW_USER_NAME_ID) || group.name !== ENTERPRISE_GROUP_NAME,
      },
      {
        accessor: '_id',
        className: 'cell-icon',
        Cell: ({ original }: { original: EnterpriseMember }) => {
          if (isEnterpriseGroup(group)) {
            return <IconButton icon="pencil" onClick={() => this.onClickModifyMember(original)} />;
          }
          return <></>;
        },
        width: 48,
        hidden: !userHasPermissionEnabled(user, PERMISSIONS.ADMIN_SECTION_SHOW_USER_UID, PERMISSIONS.ADMIN_SECTION_SHOW_USER_NAME_ID),
      },
      {
        accessor: '_id',
        className: 'cell-icon',
        Cell: ({ original }: { original: EnterpriseMember }) => {
          if (!isEnterpriseGroup(group) || original._id !== user.id) {
            return (
              <IconButton
                icon="ds-delete"
                disabled={!hasMemberRemovePermission}
                onClick={() => {
                  this.showDeleteMembersModal([original]);
                }}
              />
            );
          }
          return <></>;
        },
        width: 48,
        hidden: false,
      },
    ];
    return columns.filter(column => !column.hidden);
  };

  showDeleteMembersModal = (membersToDelete: EnterpriseMember[]) => {
    this.setState(() => ({
      showDeleteMembersModal: true,
      membersToDelete,
    }));
  };

  showAddMemberModal = () => {
    this.setState({ showAddMemberModal: true });
  };

  hideAddMemberModal = () => {
    this.setState({ showAddMemberModal: false });
  };

  showModifyMemberModal = () => {
    this.setState({ showModifyMemberModal: true });
  };

  hideModifyMemberModal = () => {
    this.setState({ showModifyMemberModal: false });
  };

  hideDeleteMembersModal = () => {
    this.setState({ showDeleteMembersModal: false });
  };

  onAddGroupMember = (memberId: string) => {
    const { group } = this.props;

    return this.props.addEnterpriseGroupMember(group._id, memberId).then(() => {
      this.hideAddMemberModal();
    });
  };

  filterMembersFromQuery = (members: EnterpriseMember[], query: string) => {
    if (!query) {
      return members;
    }

    const searchableFields = ['fname', 'lname', 'fullname', 'email', 'uid', 'nameId'];

    // Generate fullname for search purposes.
    const objs = members.map(m => ({
      ...m,
      fullname: `${m.fname} ${m.lname}`,
    }));

    return searchObjectsOnFields(query, objs, searchableFields, false);
  };

  onMembersSearch = (query: string) => {
    this.setState({ query, memberDisplayMultiplier: 1 });
  };

  onAddMember = (fname: string, lname: string, email: string, uid?: string, nameId?: string) =>
    this.props.addEnterpriseMember(fname, lname, email, uid != null ? uid : '', nameId != null ? nameId : '');

  onModifyMember = (_id: string, uid?: string, nameId?: string) => this.props.modifyEnterpriseMember(_id, uid != null ? uid : '', nameId != null ? nameId : '');

  loadMoreMembers = () => {
    const { memberDisplayMultiplier } = this.state;
    const multiplier = memberDisplayMultiplier + 1;
    this.setState({ memberDisplayMultiplier: multiplier });
  };

  onClickModifyMember = (member: EnterpriseMember): void => {
    this.setState({ memberToUpdate: member });
    this.showModifyMemberModal();
  };

  useMemberUID = () => userHasPermission(this.props.user, PERMISSIONS.ADMIN_SECTION_SHOW_USER_UID);

  useMemberNameID = () => userHasPermission(this.props.user, PERMISSIONS.ADMIN_SECTION_SHOW_USER_NAME_ID);

  render() {
    const { group, adminDetails, user } = this.props;
    const { showAddMemberModal, showModifyMemberModal, showDeleteMembersModal, query, memberDisplayMultiplier } = this.state;

    let memberSrc = [] as EnterpriseMember[];
    if (group != null) {
      memberSrc = group.members;
    } else if (adminDetails != null) {
      memberSrc = getEnterpriseGroup(adminDetails).members;
    }
    const members = this.filterMembersFromQuery(memberSrc, query).slice(0, memberDisplayMultiplier * 10);
    const hasMembers = members && members.length > 0;
    const useMemberUID = this.useMemberUID();
    const useMemberNameID = this.useMemberNameID();
    const hasMemberAddPermission = userHasPermissionEnabled(user, PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS, PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS_ADD);

    return (
      <>
        <div className="enterprise-members">
          <div className="section-headers d-flex justify-content-between">
            <SearchInput
              className="settings-search"
              value={this.state.query}
              onChange={this.onMembersSearch}
              placeholder={formatMessage('Admin.Enterprise.SearchMembers')}
            />

            <PrimaryButton disabled={!hasMemberAddPermission} onClick={this.showAddMemberModal}>
              <i className="icon-plus" />
              <FormattedMessage id="Admin.Enterprise.AddMember" />
            </PrimaryButton>
          </div>

          {hasMembers && (
            <>
              <DataTable data={members} className="results-table flex-grow-1" columns={this.getColumns()} />
              <Waypoint onEnter={() => this.loadMoreMembers()}>
                <div />
              </Waypoint>
            </>
          )}

          {group && group.members.length === 0 && (
            <GenericEmptyState header={formatMessage('Admin.Enterprise.NoMembers.Title')} body={formatMessage('Admin.Enterprise.NoMembers.Text')} />
          )}

          {group && group.members.length > 0 && !hasMembers && (
            <GenericEmptyState header={formatMessage('Admin.Enterprise.NoResults.Title')} body={formatMessage('Admin.Enterprise.NoResults.Text')} />
          )}
        </div>

        {group && group.name === ENTERPRISE_GROUP_NAME ? (
          <>
            <AddEnterpriseMemberModal
              show={showAddMemberModal}
              onCancel={this.hideAddMemberModal}
              onSave={this.onAddMember}
              useMemberUID={useMemberUID}
              useMemberNameID={useMemberNameID}
            />

            {(useMemberUID || useMemberNameID) && this.state.memberToUpdate && (
              <ModifyEnterpriseMemberModal
                user={user}
                show={showModifyMemberModal}
                onCancel={this.hideModifyMemberModal}
                onSave={this.onModifyMember}
                memberToUpdate={this.state.memberToUpdate}
              />
            )}
          </>
        ) : (
          <AddEnterpriseGroupMemberModal
            show={showAddMemberModal}
            onCancel={this.hideAddMemberModal}
            onAdd={this.onAddGroupMember}
            group={group}
            adminDetails={adminDetails}
          />
        )}

        <DestructiveModal
          open={showDeleteMembersModal}
          onOpenChange={open => !open && this.hideDeleteMembersModal()}
          title={formatMessage('Enterprise.DeleteMembersModal.Title')}
          content={
            <Stack gap="150">
              <FormattedMessage id="Enterprise.DeleteMembersModal.Body" />
              <ul>
                {this.state.membersToDelete.map(m => (
                  <li key={m._id}>{`${m.fname ?? ''} ${m.lname ?? ''}`}</li>
                ))}
              </ul>
              <FormattedMessage id="Enterprise.DeleteMembersModal.Confirmation" />
            </Stack>
          }
          confirmButtonText={formatMessage('Modal.Default.Confirm')}
          cancelButtonText={formatMessage('Common.Cancel')}
          onConfirm={this.removeSelectedMembers}
          onCancel={this.hideDeleteMembersModal}
        />
      </>
    );
  }
}

const mapStateToProps = (state: ReduxState, ownProps: ParentProps) => {
  let { group } = ownProps;

  if (!group) {
    const groups = state.enterprise.admin ? state.enterprise.admin.groups : [];
    group = groups.find(g => g.name === ENTERPRISE_GROUP_NAME) as EnterpriseAdminGroup;
  }

  return {
    user: state.auth.user as User,
    adminDetails: state.enterprise.admin,
    group,
  };
};

export default connect(mapStateToProps, {
  addEnterpriseGroupMember,
  deleteEnterpriseGroupMember,
  addEnterpriseMember,
  modifyEnterpriseMember,
  deleteEnterpriseMember,
})(EnterpriseMembers);
